import qs from 'qs'
import request from 'api/requestUtils'
import {
  cruiseLinesMap,
  cruisePromoBannersMap,
  cruiseOfferMap,
  cruiseLowestPriceByMonthMap,
  cruisesShipDeckMap,
  cruiseBookingInfoMap,
  cruiseDepartureByIdMap,
  cruiseCabinRatesMap,
  cruiseCabinRateDetailsMap,
  cruiseCabinAvailabilityMap,
  cruiseCabinHoldMap,
  cruiseCabinPricingMap,
} from './mappers/cruiseMap'
import { CruisesContract } from '@luxuryescapes/contract-svc-cruise'
import { setCruiseVendorBookingTimer } from 'components/Cruises/CruiseSessionTimer/CruiseSessionTimer'
import { offerHasValidDepartures } from 'lib/cruises/cruiseUtils'

const BASE_URI = '/api/cruise/v1'
const DEFAULT_CABIN_HOLD_TIMEOUT = 900 // 15 minutes

export async function getCruiseLines(filter?: CruisesContract.OfferParams, region?: string) {
  const queryParams = qs.stringify({
    take: 20,
    offersApproved: true,
    ...(filter || {}),
    ...(region && { region }),
  })

  const response = await request.get<
    App.ApiResponse<Array<CruisesContract.CruiseLine>>
  >(`${BASE_URI}/cruise-lines?${queryParams}`)

  return cruiseLinesMap(response.result)
}

export async function getCruiseSearchFacets(params: App.CruiseSearchFacetParams, region?: string) {
  // destinationIds is an array of strings, but it can also be a string with multiple ids separated by ','
  const destinationIds = params.destinationIds?.flatMap(id => id.split(','))

  const queryParams = qs.stringify(
    {
      facetTypes: params.facetTypes,
      destinationPlaceIds: destinationIds,
      arrivalPlaceId: params.destinationId,
      cruiseLines: params.cruiseLines,
      departurePlaceId: params.departurePlaceId,
      departureDate: params.departureDate?.substring(0, 7), // gets YYYY-MM
      departureMonths: params.departureMonths,
      durationMin: params.durationMin,
      durationMax: params.durationMax,
      rateCodes: params.rateCodes,
      includedFixedFilters: true,
      ...(region && { region }),
      ...(params.isSpecialOffers && { isSpecialOffers: params.isSpecialOffers }),
      ...(params.isLuxExclusivePromotion && { isLuxExclusivePromotion: params.isLuxExclusivePromotion }),
    },
  )

  const { result } = await request
    .get<App.ApiResponse<Array<string>>>(`/api/search/cruise/v1/facet/list?${queryParams}`)

  return result
}

export async function getCruisePromoBanners(region?: string) {
  const queryParams = qs.stringify({
    take: 30,
    active: true,
    region,
  })
  const response = await request.get<
    App.ApiResponse<Array<CruisesContract.PromoBanner>>
  >(`${BASE_URI}/promo-banners?${queryParams}`)

  return cruisePromoBannersMap(response.result)
}

function getCruiseSearchQueryString(params: App.OfferListFilters, region?: string) {
  // destinationIds is an array of strings, but it can also be a string with multiple ids separated by ','
  const destinationIds = params.destinationIds?.flatMap(id => id.split(','))
  return qs.stringify(
    {
      arrivalPlaceId: params.destinationId,
      departurePlaceId: params.departurePlaceId,
      departureDate: params.departureDate?.substring(0, 7), // gets YYYY-MM
      departureMonths: params.departureMonths?.filter(Boolean),
      durationMin: params.durationMin,
      durationMax: params.durationMax,
      cruiseLines: params.cruiseLines,
      rateCodes: params.rateCodes,
      cruiseShips: params.shipNames,
      cabinTypes: params.cabinTypes,
      // if a 0 is passed, it's meaningless, so get rid of it
      minPrice: params.minPrice || undefined,
      maxPrice: params.maxPrice || undefined,
      sortBy: params.sortBy,
      campaigns: params.campaigns?.join(','),
      ...(region && { region }),
      ...(params.isSpecialOffers && { isSpecialOffers: params.isSpecialOffers }),
      ...(params.isLuxExclusivePromotion && { isLuxExclusivePromotion: params.isLuxExclusivePromotion }),
      destinationName: params.destinationName,
      departureName: params.departureName,
      destinationPlaceIds: destinationIds,
      destinationNames: params.destinationNames,
    },
    { arrayFormat: 'indices' },
  )
}

export async function getCruiseSearchList(params: App.OfferListFilters, region?: string) {
  const queryParams = getCruiseSearchQueryString(params, region)
  return request
    .get<App.ApiResponse<Array<string>>>(`/api/search/cruise/v1/list?${queryParams}`)
    .then((response) => response.result.map(id => `cruise:${id}`))
}

export async function getCruiseSearchCount(params: App.OfferListFilters, region?: string) {
  const queryParams = getCruiseSearchQueryString(params, region)
  return request
    .get<App.ApiResponse<Array<string>>>(`/api/search/cruise/v1/count?${queryParams}`)
    .then((response) => response.result)
}

export async function getCruiseOfferSummaryBySailingId(id: string, regionCode: string) {
  const response = await request.get<
    App.ApiResponse<CruisesContract.OfferSummary>
  >(`${BASE_URI}/offers/${id}`)

  return cruiseOfferMap(response.result, regionCode)
}

export async function getCruiseLowestPriceByMonth(
  params: App.LowestPriceByMonthParams,
  region?: string,
) {
  const queryParams = qs.stringify({
    from: params.from,
    until: params.until,
    arrivalPlaceId: params.destinationId,
    departurePlaceId: params.departureId,
    cruiseLines: params.cruiseLines,
    durationMin: params.durationMin,
    durationMax: params.durationMax,
    rateCodes: params.rateCodes,
    ...(region && { region }),
  })

  const response = await request.get<
    App.ApiResponse<CruisesContract.LowestPriceByMonth[]>
  >(`/api/search/cruise/v1/lowest-price-by-month?${queryParams}`)

  return cruiseLowestPriceByMonthMap(response.result, params.from)
}

export async function getCruiseOffersBySailingId(ids: Array<string>, regionCode: string) {
  return await Promise.all(ids.map(id =>
    getCruiseOfferSummaryBySailingId(id, regionCode),
  ))
}

export async function getCruiseOffer(id: string, region: string, currencyCode?: string) {
  const params = qs.stringify({
    currencyCode,
    region,
  })

  const response = await request.get<
    App.ApiResponse<CruisesContract.Offer>
  >(`${BASE_URI}/offers/${id.replace('cruise:', '')}?${params}`)

  return cruiseOfferMap(response.result, region)
}

export async function getValidCruiseOffer(id: string, region: string, currencyCode?: string) {
  const offer = await getCruiseOffer(id, region, currencyCode)
  const hasValidDepartures = offerHasValidDepartures(offer)

  if (!hasValidDepartures) {
    throw new Error(`No valid departures found for offer ${id}`)
  }

  return offer
}

export async function getCruiseShipDeck(id: string) {
  const response = await request.get<
    App.ApiResponse<CruisesContract.ShipDeck>
  >(`${BASE_URI}/ship-decks/${id}`)

  return cruisesShipDeckMap(response.result)
}

export async function getCruiseBookingById(bookingId: string) {
  const response = await request.get<
    App.ApiResponse<CruisesContract.BookingOrderResponse>
  >(`${BASE_URI}/bookings/${bookingId}`, { credentials: 'include' })

  return cruiseBookingInfoMap(response.result)
}

export async function getCruiseDepartureById(departureId: string) {
  const response = await request.get<
    App.ApiResponse<CruisesContract.DepartureByIdResponse>
  >(`${BASE_URI}/departures/${departureId}`)

  return cruiseDepartureByIdMap(response.result)
}

// BOOKING FLOW STEPS

/*
  1st mandatory booking flow request:
  send the number of travelers and the departure ID.
  The rate information for each cabin type will be returned
  and a session ID will also be returned which must be sent
  for all other requests in the booking flow.
*/

export async function bookingCabinRates(
  params: CruisesContract.CabinRateBody,
  currencyCode: string,
  region: string,
): Promise<App.CruiseCabinRateResponse> {
  const queryParams = qs.stringify({
    currencyCode,
    region,
  })

  const res = await request.post<
App.ApiResponse<CruisesContract.CabinRateResponse>,
    CruisesContract.CabinRateBody>(`${BASE_URI}/booking/cabin-rate?${queryParams}`, params, { credentials: 'include' })

  if (res.errorToRender) return res.errorToRender

  const cabinRates = cruiseCabinRatesMap(res.result)
  setCruiseVendorBookingTimer(cabinRates.cabinHoldTimeout || DEFAULT_CABIN_HOLD_TIMEOUT)
  return cabinRates
}

/*
  Booking Flow request support, isn't mandatory.
  Send a componentId to get the details about that rate.
*/

export async function bookingCabinRateDetails(
  params: CruisesContract.CabinRateDetailsBody,
  region: string,
): Promise<App.CruiseCabinRateDetailsResponse> {
  const queryParams = qs.stringify({ region })
  const { result } = await request.post<
    App.ApiResponse<CruisesContract.CabinRateDetailsResponse>,
    CruisesContract.CabinRateDetailsBody>(`${BASE_URI}/booking/cabin-rate-details?${queryParams}`, params, { credentials: 'include' })

  return cruiseCabinRateDetailsMap(result.cabinRateDetails[0])
}

type CabinAvailabilityRequestParams = CruisesContract.CabinAvailabilityBody & { sessionId: string }

/* 2nd - It is part of the `Cabin Location` of the booking flow.
It takes in a cabin category id and returns a list of available cabins. */
export async function cabinAvailability(
  params: CruisesContract.CabinAvailabilityBody,
  providerSessionId: string,
  currencyCode: string,
  region: string,
): Promise<App.CruiseCabinAvailabilityResponse> {
  const queryParams = qs.stringify({
    currencyCode,
    region,
  })

  const res = await request.post<
  App.ApiResponse<CruisesContract.CabinAvailability>,
    CabinAvailabilityRequestParams>(
      `${BASE_URI}/cabin/availability?${queryParams}`,
      {
        cabinCode: params.cabinCode,
        sessionId: providerSessionId,
        categoryId: params.categoryId,
        rateCodeDetails: params.rateCodeDetails,
        shipId: params.shipId,
        isNccfIncluded: params.isNccfIncluded,
        isTaxIncluded: params.isTaxIncluded,
        rateCodeDescription: params.rateCodeDescription,
      },
      { credentials: 'include' },
    )

  if (res.errorToRender) return res.errorToRender
  return cruiseCabinAvailabilityMap(res.result)
}

/* 3rd - It is part of the `Cabin Location` on the booking flow.
it holds a cabin for a sessioned user and returns it's base configuration */
export async function bookingCabinHold(
  params: CruisesContract.CabinHoldBody,
  providerSessionId: string,
  currencyCode: string,
  region: string,
): Promise<App.CruiseCabinHold> {
  const queryParams = qs.stringify({
    currencyCode,
    region,
  })

  const { result } = await request.post<
    App.ApiResponse<CruisesContract.CabinHoldResponse>,
    CruisesContract.CabinHoldBody>(
      `${BASE_URI}/booking/cabin-hold?${queryParams}`,
      {
        cabinNumber: params.cabinNumber,
        sessionId: providerSessionId,
      },
      { credentials: 'include' },
    )

  return cruiseCabinHoldMap(result)
}

/* 4rd - It is part of the `Traveller details` on the booking flow.
It configures a cabin for a sessioned user and returns it's final pricing */
export async function bookingCabinPricing(
  params: CruisesContract.CabinPricingBody,
  providerSessionId: string,
  currencyCode: string,
  region: string,
): Promise<App.CruiseCabinPricing> {
  const queryParams = qs.stringify({
    currencyCode,
    region,
  })

  const { result } = await request.post<
    App.ApiResponse<CruisesContract.CabinPricingResponse>,
    CruisesContract.CabinPricingBody>(
      `${BASE_URI}/booking/cabin-pricing?${queryParams}`,
      {
        ...params,
        sessionId: providerSessionId,
      },
      { credentials: 'include' },
    )

  return cruiseCabinPricingMap(result)
}

/** SUP - It fetches the details of a cabin */
export async function bookingCabinDetails(
  params: CruisesContract.CabinDetailsBody,
  providerSessionId: string,
  region: string,
): Promise<App.CruiseCabinDetails> {
  const queryParams = qs.stringify({
    cabinNumber: params.cabinNumber,
    sessionId: providerSessionId,
    region,
  })
  const { result } = await request.get<App.ApiResponse<CruisesContract.CabinDetailsResponse>>(
    `${BASE_URI}/booking/cabin-details?${queryParams}`,
    { credentials: 'include' },
  )

  return result
}

/** SUP - It releases all held cabins within the current sessionId */
export async function bookingCabinRelease(
  providerSessionId: string,
  region: string,
): Promise<App.CruiseCabinRelease> {
  const queryParams = qs.stringify({ region })
  const { result } = await request.post<
    App.ApiResponse<CruisesContract.CabinReleaseResponse>,
    CruisesContract.CabinReleaseBody>(
      `${BASE_URI}/booking/cabin-release?${queryParams}`,
      { sessionId: providerSessionId },
      { credentials: 'include' },
    )

  return {
    success: result.success,
  }
}
